import { useEffect, useRef, useState } from 'react';
import { listAnbiyam } from '@/api/anbiyam';
import type { Anbiyam } from '@/api/anbiyam';
import {
  createExportJob, listExportJobs, getExportJob,
} from '@/api/exportJobs';
import type { ExportJob, ExportGroupingMode, ExportJobType } from '@/api/exportJobs';

const currentYear = new Date().getFullYear();
const SUB_START_YEAR = 2023;
const YEAR_OPTIONS = Array.from(
  { length: currentYear - SUB_START_YEAR + 2 },
  (_, i) => SUB_START_YEAR + i,
);

const POLL_INTERVAL_MS = 4000;

const StatusPill = ({ status }: { status: ExportJob['status'] }) => {
  const cls =
    status === 'Ready'    ? 'bg-green-100 text-green-800' :
    status === 'Failed'   ? 'bg-red-100 text-red-700' :
    status === 'Queued'   ? 'bg-gray-100 text-gray-600' :
                             'bg-blue-100 text-blue-800';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full font-georgia ${cls}`}>
      {(status === 'Rendering' || status === 'Zipping') && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {status}
    </span>
  );
};

export default function BulkExportPage() {
  const [anbiyams, setAnbiyams]           = useState<Anbiyam[]>([]);
  const [selectedAnbiyams, setSelectedAnbiyams] = useState<Set<number>>(new Set());
  const [selectedYears, setSelectedYears] = useState<Set<number>>(new Set([currentYear - 1]));
  const [grouping, setGrouping] = useState<ExportGroupingMode>('per-year');
  const [jobType, setJobType] = useState<ExportJobType>('statement');

  const [jobs, setJobs]         = useState<ExportJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');

  const pollingIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    listAnbiyam({ pageSize: 50 }).then((r) => setAnbiyams(r.items)).catch(() => setError('Failed to load anbiyams'));
    refreshJobs();
  }, []);

  const refreshJobs = () => {
    listExportJobs({ pageSize: 25 })
      .then((r) => {
        setJobs(r.items);
        r.items.forEach((j) => {
          if (j.status === 'Queued' || j.status === 'Rendering' || j.status === 'Zipping') {
            pollingIds.current.add(j.id);
          } else if (j.status === 'Ready' && !j.downloadUrl) {
            // The list endpoint doesn't presign download URLs (that's only
            // done on demand by export-job-status.js) — fetch it once so
            // "Download ZIP" works for jobs that were already Ready when
            // this page loaded, not just ones finished during this visit.
            getExportJob(j.id).then((full) => {
              setJobs((prev) => prev.map((p) => (p.id === full.id ? full : p)));
            }).catch(() => {});
          }
        });
      })
      .catch(() => setError('Failed to load job history'))
      .finally(() => setLoadingJobs(false));
  };

  // Poll every in-flight job until it settles.
  useEffect(() => {
    const interval = setInterval(() => {
      if (pollingIds.current.size === 0) return;
      pollingIds.current.forEach((id) => {
        getExportJob(id).then((job) => {
          setJobs((prev) => prev.map((j) => (j.id === job.id ? job : j)));
          if (job.status === 'Ready' || job.status === 'Failed') {
            pollingIds.current.delete(id);
          }
        }).catch(() => pollingIds.current.delete(id));
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const toggleAnbiyam = (id: number) => {
    setSelectedAnbiyams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleYear = (y: number) => {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y); else next.add(y);
      return next;
    });
  };

  const allAnbiyamsSelected = anbiyams.length > 0 && selectedAnbiyams.size === anbiyams.length;
  const toggleAllAnbiyams = () => {
    setSelectedAnbiyams(allAnbiyamsSelected ? new Set() : new Set(anbiyams.map((a) => a.anbiyamId)));
  };

  const allYearsSelected = selectedYears.size === YEAR_OPTIONS.length;
  const toggleAllYears = () => {
    setSelectedYears(allYearsSelected ? new Set() : new Set(YEAR_OPTIONS));
  };

  const statementCount = selectedAnbiyams.size * selectedYears.size;

  const startJob = async () => {
    setError('');
    if (selectedAnbiyams.size === 0) return setError('Select at least one anbiyam');
    if (selectedYears.size === 0)    return setError('Select at least one year');

    setSubmitting(true);
    try {
      const job = await createExportJob({
        anbiyamIds: Array.from(selectedAnbiyams),
        years:      Array.from(selectedYears),
        grouping,
        jobType,
      });
      pollingIds.current.add(job.id);
      setJobs((prev) => [job, ...prev]);
    } catch {
      setError('Failed to start export job — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-georgia font-bold text-navy mb-1">Bulk Export</h1>
      <p className="text-sm text-gray-600 mb-6 max-w-2xl">
        Choose any set of anbiyams and years once — the system generates every
        PDF in the background and packages them into one ZIP, instead of
        exporting one anbiyam and one year at a time.
      </p>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 font-georgia">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Export type */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-sm font-georgia font-semibold text-navy mb-3">1. Choose export type</h2>
            <div className="flex flex-col gap-2 text-sm font-georgia">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="jobType" className="accent-teal mt-0.5" checked={jobType === 'statement'} onChange={() => setJobType('statement')} />
                <span>
                  <span className="block font-semibold">Subscription Statement</span>
                  <span className="block text-xs text-gray-500">Family Code, Head of Family, and Jan–Dec paid status for the office record.</span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="jobType" className="accent-teal mt-0.5" checked={jobType === 'collection-sheet'} onChange={() => setJobType('collection-sheet')} />
                <span>
                  <span className="block font-semibold">Family Collection Sheet</span>
                  <span className="block text-xs text-gray-500">Adds existing cemetery records and blank fillable boxes for anything missing, so a volunteer or family can complete it by hand during a house visit.</span>
                </span>
              </label>
            </div>
          </section>

          {/* Anbiyams */}
          <section className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-georgia font-semibold text-navy">2. Choose anbiyams</h2>
              <button
                type="button"
                onClick={toggleAllAnbiyams}
                className="text-xs font-georgia font-semibold text-teal hover:underline"
              >
                {allAnbiyamsSelected ? 'Clear all' : `Select all ${anbiyams.length}`}
              </button>
            </div>
            <div className="border rounded max-h-64 overflow-y-auto divide-y">
              {anbiyams.map((a) => (
                <label key={a.anbiyamId} className="flex items-center gap-2.5 px-3 py-2 text-sm font-georgia hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-teal w-4 h-4"
                    checked={selectedAnbiyams.has(a.anbiyamId)}
                    onChange={() => toggleAnbiyam(a.anbiyamId)}
                  />
                  {a.anbiyamName}
                  {typeof a.familyCount === 'number' && (
                    <span className="ml-auto text-xs text-gray-500">{a.familyCount} families</span>
                  )}
                </label>
              ))}
              {anbiyams.length === 0 && (
                <p className="px-3 py-4 text-sm text-gray-500 font-georgia">Loading anbiyams…</p>
              )}
            </div>
          </section>

          {/* Years */}
          <section className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-georgia font-semibold text-navy">3. Choose years</h2>
              <button
                type="button"
                onClick={toggleAllYears}
                className="text-xs font-georgia font-semibold text-teal hover:underline"
              >
                {allYearsSelected ? 'Clear all' : 'Select all years'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {YEAR_OPTIONS.map((y) => (
                <label key={y} className="cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={selectedYears.has(y)}
                    onChange={() => toggleYear(y)}
                  />
                  <span className="block px-4 py-1.5 rounded-full border text-sm font-georgia text-gray-600 peer-checked:bg-teal peer-checked:text-white peer-checked:border-teal">
                    {y}
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Output */}
          <section className="bg-white border rounded-lg p-4">
            <h2 className="text-sm font-georgia font-semibold text-navy mb-3">4. Output</h2>
            <div className="flex flex-col gap-2 text-sm font-georgia">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="grouping" className="accent-teal" checked={grouping === 'per-year'} onChange={() => setGrouping('per-year')} />
                One PDF per anbiyam per year
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="grouping" className="accent-teal" checked={grouping === 'combined'} onChange={() => setGrouping('combined')} />
                One combined PDF per anbiyam
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Files are packaged into a single ZIP using the same shrine letterhead as today's individual export.
            </p>
          </section>
        </div>

        {/* Summary */}
        <div>
          <div className="bg-teal/5 border border-teal/30 rounded-lg p-4 sticky top-4">
            <h2 className="text-xs font-georgia font-semibold uppercase tracking-wide text-gray-500 mb-3">Job summary</h2>
            <dl className="space-y-2 text-sm font-georgia mb-3">
              <div className="flex justify-between"><dt className="text-gray-600">Anbiyams selected</dt><dd className="font-semibold">{selectedAnbiyams.size} of {anbiyams.length}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-600">Years selected</dt><dd className="font-semibold">{selectedYears.size}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-600">Statements to generate</dt><dd className="font-semibold">{statementCount}</dd></div>
            </dl>
            <button
              type="button"
              onClick={startJob}
              disabled={submitting || statementCount === 0}
              className="w-full bg-teal text-white font-georgia font-semibold text-sm py-2.5 rounded hover:bg-teal/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Starting…' : 'Start export job'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Runs in the background. You'll see it below once it's ready to download — no need to keep this page open.
            </p>
          </div>
        </div>
      </div>

      {/* Job history */}
      <section className="mt-8">
        <h2 className="text-sm font-georgia font-semibold text-navy mb-3">Job history</h2>
        <div className="bg-white border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm font-georgia">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">Job</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Scope</th>
                <th className="px-4 py-2">Files</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Started</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const anbiyamIds: number[] = JSON.parse(job.anbiyam_ids || '[]');
                const years: number[]      = JSON.parse(job.years || '[]');
                return (
                  <tr key={job.id} className="border-t">
                    <td className="px-4 py-2.5 font-mono text-xs">#EXP-{String(job.id).padStart(4, '0')}</td>
                    <td className="px-4 py-2.5 text-xs">{job.job_type === 'collection-sheet' ? 'Collection Sheet' : 'Statement'}</td>
                    <td className="px-4 py-2.5">{anbiyamIds.length} anbiyam{anbiyamIds.length !== 1 ? 's' : ''} · {years.join(', ')}</td>
                    <td className="px-4 py-2.5">{job.file_count ?? '—'}</td>
                    <td className="px-4 py-2.5"><StatusPill status={job.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(job.created_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      {job.status === 'Ready' && job.downloadUrl && (
                        <a href={job.downloadUrl} className="text-teal font-semibold hover:underline text-xs">Download ZIP</a>
                      )}
                      {job.status === 'Failed' && (
                        <span className="text-red-600 text-xs" title={job.error_message ?? ''}>Failed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loadingJobs && jobs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No export jobs yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
